import { socialNetworkPrettyLabel } from "./socialNetworkCatalog"

const GOAL_CATEGORY_GROUPS = [
  { type: "Visibility", id: "visibility", label: "Visibility" },
  { type: "Engagement", id: "engagement", label: "Engagement" },
  { type: "Growth", id: "growth", label: "Growth" },
  { type: "Conversions & Revenue", id: "conversions", label: "Conversions & Revenue" },
]

export function metricNameToMetricId(metricName) {
  const parts = String(metricName || "")
    .trim()
    .split("_")
    .filter(Boolean)
  if (parts.length === 0) return ""
  return parts
    .map((part, index) =>
      index === 0 ? part : `${part.charAt(0).toUpperCase()}${part.slice(1)}`,
    )
    .join("")
}

export function socialNetworkDescriptionToPlatform(description) {
  return socialNetworkPrettyLabel(description) || "Unknown"
}

function inferMetricFormat(metricName, displayName) {
  const combined = `${metricName} ${displayName}`.toLowerCase()
  if (combined.includes("rate") || combined.includes("engagement %")) return "percent"
  if (combined.includes("revenue") || combined.includes("roas") || combined.includes("spend")) {
    return "currency"
  }
  return "number"
}

function categoryGroupId(type) {
  return (
    String(type || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "other"
  )
}

/** GET /metric-goals — normalize list items (raw API or already-normalized rows). */
export function normalizeMetricGoals(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : []

  return list
    .filter((item) => item && (item.id != null || item.apiId != null))
    .map((item) => ({
      apiId: Number(item.apiId ?? item.id),
      metricName: String(item.metricName ?? item.metric_name ?? "").trim(),
      name: String(item.name ?? "").trim(),
      categoryType: String(item.categoryType ?? item.type ?? "Engagement").trim(),
      socialNetworkTypeId: item.socialNetworkTypeId ?? item.socialNetworkType?.id ?? null,
      socialNetworkDescription: String(
        item.socialNetworkDescription ?? item.socialNetworkType?.description ?? "",
      ).trim(),
      metricGoalTypeId:
        item.metricGoalTypeId != null && Number.isFinite(Number(item.metricGoalTypeId))
          ? Number(item.metricGoalTypeId)
          : null,
    }))
    .filter((row) => Number.isFinite(row.apiId) && row.metricName && row.name)
}

const GOAL_TYPE_CATEGORY_ORDER = GOAL_CATEGORY_GROUPS.map((g) => g.type)

function dedupeCatalogRows(rows) {
  const seen = new Set()
  const out = []
  for (const row of rows) {
    const id = metricNameToMetricId(row.metricName)
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(row)
  }
  return out
}

function orderedCategoryGroups(byCategory) {
  const knownTypes = new Set(GOAL_CATEGORY_GROUPS.map((g) => g.type))
  const known = GOAL_CATEGORY_GROUPS.filter((group) => byCategory.has(group.type))
  const extras = [...byCategory.keys()]
    .filter((type) => !knownTypes.has(type))
    .sort((a, b) => a.localeCompare(b))
    .map((type) => ({ type, id: categoryGroupId(type), label: type }))
  return [...known, ...extras]
}

/** Build dashboard goal picker groups from API metrics. */
export function buildGoalMetricGroupsFromApi(goals) {
  const list = dedupeCatalogRows(normalizeMetricGoals(goals))
  if (list.length === 0) return []

  const byCategory = new Map()
  for (const row of list) {
    const key = row.categoryType || "Engagement"
    if (!byCategory.has(key)) byCategory.set(key, [])
    byCategory.get(key).push(row)
  }

  return orderedCategoryGroups(byCategory)
    .map((group) => {
      const rows = byCategory.get(group.type) ?? []
      return {
        id: group.id,
        label: group.label,
        categoryType: group.type,
        metrics: rows.map((row) => {
          const id = metricNameToMetricId(row.metricName)
          return {
            id,
            apiId: row.apiId,
            metricName: row.metricName,
            name: row.name,
            categoryType: row.categoryType,
            metricGoalTypeId: row.metricGoalTypeId,
            platform: socialNetworkDescriptionToPlatform(row.socialNetworkDescription),
            socialNetworkDescription: row.socialNetworkDescription,
            socialNetworkTypeId: row.socialNetworkTypeId,
            source: "Organic",
            format: inferMetricFormat(row.metricName, row.name),
            defaultTarget: 0,
            defaultPeriod: "monthly",
            current: 0,
            helper: "",
          }
        }),
      }
    })
    .filter((group) => group.metrics.length > 0)
}

export { GOAL_TYPE_CATEGORY_ORDER, GOAL_CATEGORY_GROUPS }
